<template>
    <model-modal
        component="v-spa-mixed-modal"
        :model-class="modelClass" 
        modal-type="form"
        @opening="onOpening"
        @opened="onOpened"
        @resetted="onReset"
        ref="modal"
    >
        <template v-slot:default="slotProps">
            <h2 class="mb-4 text-xl">
                {{ slotProps.model ? $t('opportunity.form.title.edit') : $t('opportunity.form.title.create') }}
            </h2>
            <form @submit.prevent="submit">
                <div class="mb-6">
                    <v-spa-input
                        ref="inputTitle"
                        :label="$t('opportunity.form.labels.title')"
                        id="create-opportunity-form-title"
                        :form="form"
                        input-name="title"
                    ></v-spa-input>
                </div>

                <!-- <v-spa-element
                    :form="form"
                    id="create-opportunity-contact"
                    input-name="contact_uuid"
                    :label="$t('form.labels.contact.name')"
                >
                    <v-spa-meilisearch-contacts-search v-model="searchedContact" />
                </v-spa-element> -->

                <v-spa-element id="create-opportunity-form-source" :form="form" input-name="source" :label="$t('opportunity.form.labels.source')">
                    <select v-model="form.source" :class="$spa.css.form.select" id="create-opportunity-form-source">
                        <option :value="source" v-for="source in ['trustup', 'website', 'facebook', 'other']" :key="source">
                            {{ $t(`opportunities.source.${source}`) }}
                        </option>
                    </select>
                </v-spa-element>

                <v-spa-meilisearch-contacts-search-form-compatible
                    :form="form"
                    id="create-opportunity-contact"
                    input-name="contact_uuid"
                    :on-init="initContact"
                    :on-create="onContactCreate"
                    :on-edit="onContactEdit"
                    :on-contact-updated="setContact"
                />
                
                <v-spa-address-input :label="$t('opportunity.form.labels.address')" id="create-opportunity-form-address" :form="form" input-name="address"></v-spa-address-input>
                
                <v-spa-element id="opportunity-form-manager_id" :form="form" input-name="manager_id" :label="$t('opportunity.form.labels.users')">
                    <select v-model="form.manager_id" :class="$spa.css.form.select" id="opportunity-form-manager_id">
                        <option :value="user.id" v-for="user in $store.getters.usersWithAdminRole" :key="user.id">
                            {{ user.name }}
                        </option>
                    </select>
                </v-spa-element>

                <v-spa-textarea :label="$t('opportunity.form.labels.notes')" id="create-opportunity-form-notes" :form="form" input-name="notes"></v-spa-textarea>
                
                <div class="flex justify-end max-w-input">
                    <v-spa-loading-button :loading="form.isLoading()">
                        {{ slotProps.model ? $t('opportunity.form.buttons.edit') : $t('opportunity.form.buttons.create') }}
                    </v-spa-loading-button>
                </div>
            </form>
        </template>
    </model-modal>
</template>

<script>
    import { mapState } from "vuex";
    import Form from '~spa/classes/Form';
    import DatePicker from 'v-calendar/lib/components/date-picker.umd';
    import { Type } from "@spa/api/app";
    import ModelModal from '@spa/apps/trustup-pro-base-js/components/Model/Modal';
    import Opportunity from '@spa/apps/trustup-pro-worksite-js/models/Opportunity';

    export default {
        components: {
            ModelModal,
            DatePicker,
        },
        
        data() {
            return {
                modelClass: Opportunity,
                colors: null,
                form: new Form({
                    title: '',
                    address: '',
                    contact_uuid: null,
                    manager_id: [],
                    source: 'other',
                    notes: ''
                }),
                range: null,
                contact: null
            }
        },

        methods: {
            async onOpening(data)
            {
                this.form.manager_id = this.$store.state.user.id;

                if ( this.$refs.modal.model ) {
                    this.form.fill( this.$refs.modal.model.data() );
                    this.form.manager_id = this.$refs.modal.model.getManagers()[0]?.id;
                }
            },

            async onOpened()
            {
                await this.$nextTick();
                await this.$nextTick();
                this.$refs.inputTitle.$refs.input.focus();
            },

            onReset()
            {
                this.form.reset();
                this.form.stop();
            },

            setContact(contact)
            {
                this.contact = contact;
                return this;
            },

            useContactAddress()
            {
                this.$refs.addressInput.$refs.autocomplete.update(
                    `${this.contact.address_line_1 || ''} ${this.contact.address_line_2 || ''} ${this.contact.postal_code || ''} ${this.contact.city || ''} ${this.contact.country || ''}`
                );
                this.$refs.addressInput.$refs.autocomplete.focus();
                this.$nextTick(() => {
                    window.google.maps.event.trigger(this.$refs.addressInput.$refs.autocomplete.$el, 'focus', {});
                });
            },

            submit()
            {
                if ( this.form.isLoading() ) {
                    return;
                }
                
                this.form.load();
                let data = this.form.data();
                data.managers_id = [data.manager_id];
                delete data.manager_id;
                let action = this.$refs.modal.model ? 'update' : 'store';
                let params = this.$refs.modal.model ? {uuid: this.$refs.modal.model.uuid, data: data} : data;

                window.Worksite.endpoints.opportunity[action](params).then(opportunity => {
                    /** If contact was not yet processed by meilisearch queue, fake it in response. */
                    if (!opportunity.contact && opportunity.contact_uuid) opportunity.contact = this.contact;
                    this.$refs.modal.close();
                })
                .catch((data) => {
                    this.form.onFail(data);
                });
            },

            initContact(api)
            {
                if (!this.opportunity?.contact) return;
                api.setContact(this.opportunity.contact);
            },

            /**
             * @param {import('@spa/types/bus').EventBusListenerParams<'contacts.create', import('@spa/types/bus').PackageEventBusEvents>} params
             * @returns {import('@spa/types/bus').EventBusListenerParams<'contacts.create', import('@spa/types/bus').PackageEventBusEvents>}
             */
            onContactCreate(params)
            {
                /** @type {(contact: import('@spa/types/api/app/contact').ContactNotPersistedInterface) => import('@spa/types/api/app/contact').ContactNotPersistedInterface)} */
                const onInit = contact => {
                    contact.types = new Type().translate([{ name: 'customer' }]);
                    return params.onInit(contact);
                }

                return {
                    onInit,
                    options: this.createOrEditOptions(params)
                }
            },

            /**
             * @param {import('@spa/types/bus').EventBusListenerParams<'contacts.edit', import('@spa/types/bus').PackageEventBusEvents>} params
             * @returns {import('@spa/types/bus').EventBusListenerParams<'contacts.edit', import('@spa/types/bus').PackageEventBusEvents>}
             */
            onContactEdit(params)
            {
                return {
                    ...params,
                    options: this.createOrEditOptions(params)
                }
            },

            /**
             * @param {import('@spa/types/bus').EventBusListenerParams<'contacts.edit', import('@spa/types/bus').PackageEventBusEvents>} params
             * @returns {import('@spa/types/components').CompleteContactFormOptions}
             */
            createOrEditOptions(params)
            {
                return {
                    ...params?.options,
                    hide: {
                        ...params?.options?.hide,
                        types: true
                    }
                }
            },
        }
    }
</script>